import {
  type AuthError,
  type MfaMethod,
  SESSION_END_REASONS,
  type SessionEndReason,
  type SignInStep,
  signInStartRequestSchema,
} from "@esi/shared";
import {
  ChevronRight,
  ExternalLink,
  KeyRound,
  Languages,
  type LucideIcon,
  Smartphone,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "@/components/Avatar";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { CodeField } from "../account/CodeField.tsx";
import { NewPasswordField, passwordLongEnough } from "../account/PasswordField.tsx";
import { TotpSetup } from "../account/TotpSetup.tsx";
import { BrandMark, useTenantLogo } from "../brand/BrandMark.tsx";
import { EsiLockup, LOCKUP_MIN_WIDTH, lockupClearSpace } from "../brand/EsiMark.tsx";
import { currentLocale, LOCALES, type Locale, setLocale, t as translate } from "../i18n/i18n.ts";
import { Link, RESET_PASSWORD_PATH, type ResetHandoff } from "../router.tsx";
import { readStep, retryMinutes, type StepResult, startFlow } from "./pre-session.ts";
import { safeReturnTo, signInNavigation, signInReturnPath } from "./session-ended.ts";
import { type SignInRealm, signInApi } from "./sign-in-api.ts";
import { createPasskey, getPasskey, webAuthnSupported } from "./webauthn.ts";

/**
 * A page outside the shell (sign-in, consent, device, account link, unreachable server): the
 * product mark (the ESI lockup, or a signed-in tenant's logo) in the page's top corner, one
 * centred column for the step, and a quiet foot with the language switch. The mark names the
 * product, so the heading never repeats it. Not a link: there is no home to go to yet.
 * Without a `title`, the children bring their own heading (a sign-in step names itself).
 * Under a tenant's logo, one quiet line keeps ESI named (co-branding); no ESI mark beside it.
 */
export function StandaloneState({
  title,
  lead,
  icon,
  console: onConsole = false,
  children,
}: {
  title?: string;
  lead?: ReactNode;
  icon?: LucideIcon;
  /** The console website's page: the ESI lockup with the "Console" label, never a tenant's. */
  console?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const tenant = useTenantLogo();
  return (
    <div className="flex min-h-dvh flex-col bg-surface">
      <header className="flex h-16 shrink-0 flex-col justify-center gap-1 px-4 sm:px-8">
        {onConsole ? (
          <ConsoleLockup />
        ) : (
          <>
            <BrandMark width={96} tenantClassName="h-7 max-w-40" />
            {tenant ? <p className="text-xs text-fg-muted">{t("signIn.onEsi")}</p> : null}
          </>
        )}
      </header>
      <main className="flex flex-1 flex-col items-center px-4 pt-[min(12vh,7rem)] pb-12">
        <div className="flex w-full max-w-sm flex-col gap-6">
          {title ? <StandaloneHeading title={title} lead={lead} icon={icon} /> : null}
          {children}
        </div>
      </main>
      <StandaloneFoot />
    </div>
  );
}

/** The console's mark on its standalone pages: the ESI lockup and the "Console" label. */
function ConsoleLockup() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center">
      <EsiLockup width={LOCKUP_MIN_WIDTH} />
      <span
        className="text-xs font-medium text-fg-muted"
        style={{ marginInlineStart: lockupClearSpace(LOCKUP_MIN_WIDTH) }}
      >
        {t("console.badge")}
      </span>
    </div>
  );
}

/** A standalone page's heading: an optional 40px icon tile (outcomes), the title, its lead. */
export function StandaloneHeading(props: { title: string; lead?: ReactNode; icon?: LucideIcon }) {
  const Icon = props.icon;
  return (
    <div className="flex flex-col gap-2">
      {Icon ? (
        <span className="mb-2 grid size-10 place-items-center rounded-lg border border-line bg-surface-raised text-fg-muted shadow-xs">
          <Icon aria-hidden="true" className="size-5" strokeWidth={1.5} />
        </span>
      ) : null}
      <h1 className="text-2xl font-semibold tracking-tight text-balance text-fg">{props.title}</h1>
      {props.lead ? (
        <p className="text-sm leading-relaxed text-fg-secondary">{props.lead}</p>
      ) : null}
    </div>
  );
}

const LANGUAGE_NAMES: Record<Locale, string> = { en: "English", fr: "Français" };

/** The foot of a standalone page: the language, switchable before anyone is signed in. */
function StandaloneFoot() {
  const { t } = useTranslation();
  const current = currentLocale();
  return (
    <footer className="flex shrink-0 items-center justify-center px-4 py-5 sm:justify-start sm:px-8">
      {/* biome-ignore lint/a11y/useSemanticElements: a group of two toggles, not a fieldset */}
      <div role="group" aria-label={t("signIn.language")} className="flex items-center gap-1">
        <Languages aria-hidden="true" className="mr-1 size-3.5 text-fg-muted" strokeWidth={1.75} />
        {LOCALES.map((locale) => (
          <button
            key={locale}
            type="button"
            lang={locale}
            aria-pressed={current === locale}
            onClick={() => void setLocale(locale)}
            className={cn(
              "rounded-sm px-1.5 py-0.5 text-xs transition-colors duration-(--duration-fast)",
              current === locale ? "font-medium text-fg" : "text-fg-muted hover:text-fg-secondary",
            )}
          >
            {LANGUAGE_NAMES[locale]}
          </button>
        ))}
      </div>
    </footer>
  );
}

/** Where a sign-in stands; every step past the email keeps it visible. */
export type FlowStep =
  | { kind: "email" }
  | { kind: "redirecting"; host: string }
  /** A domain whose mode allows ESI credentials (#419, D6): its IdP, or a password or passkey. */
  | { kind: "choice"; displayName: string }
  | { kind: "credentials"; passkey: boolean }
  | { kind: "totp" }
  | { kind: "new_password" }
  | { kind: "passkey"; options: Record<string, unknown> }
  | { kind: "enrol"; methods: MfaMethod[] };

/** A refusal of the pre-session API, in plain words (never which credential was wrong). */
export function authErrorMessage(result: { status: number; error: AuthError | null }): string {
  switch (result.error?.code) {
    case "invalid_credentials":
      return translate("signIn.errors.invalid");
    case "throttled":
      return translate("signIn.errors.throttled", { count: retryMinutes(result.error) });
    case "flow_expired":
      return translate("signIn.errors.flowExpired");
    case "invalid_code":
      return translate("signIn.errors.invalidCode");
    case "invalid_link":
      return translate("link.invalid");
    case "password_already_set":
      return translate("link.passwordSet");
    case "weak_password":
      return translate("password.weak");
    default:
      return result.status === 0 ? translate("errors.network") : translate("errors.unexpected");
  }
}

/** The host a redirect goes to: the sign-in service that hands over to the organisation's IdP. */
function hostOf(url: string): string {
  try {
    return new URL(url, window.location.href).host;
  } catch {
    return url;
  }
}

const isEndReason = (value: string | null): value is SessionEndReason =>
  SESSION_END_REASONS.includes(value as SessionEndReason);

const network = { ok: false, status: 0, error: null } as const satisfies StepResult;

/** Runs a pre-session call; a network failure becomes a `status: 0` refusal. */
async function call(request: () => Promise<Parameters<typeof readStep>[0]>): Promise<StepResult> {
  try {
    return await readStep(await request());
  } catch {
    return network;
  }
}

/**
 * The steps of a sign-in on ESI's page, once the browser has a pre-session flow: email,
 * then a redirect to the company IdP or the password (or a passkey), the second factor or its
 * enrolment, and finally a full page load to where the person was going.
 */
export function SignInFlow(props: {
  /** Where to start: a link page continues after setting the password. */
  initial?: { email: string; step: SignInStep };
  /** Shown above the form (a sign-in error or the session-expired notice). */
  notice?: ReactNode;
  /** The console's own sign-in (operators): no reset link, no passkey, its own copy. */
  realm?: SignInRealm;
}) {
  const { t } = useTranslation();
  const realm = props.realm ?? "tenant";
  const auth = signInApi(realm);
  const id = useId();
  // `login_hint`: the API sent a client that knows the choice back here, the e-mail filled.
  const [email, setEmail] = useState(() => props.initial?.email ?? loginHint(realm));
  const [step, setStep] = useState<FlowStep>({ kind: "email" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  /** The authenticator setup could not load: its row comes back as the way to retry. */
  const [setupFailed, setSetupFailed] = useState(false);
  const returnTo = useRef(signInReturnPath());

  /** Whether the credentials step offers a passkey (kept for going back to it). */
  const offerPasskey = useRef(false);

  /** Moves to the step the server named, or explains its refusal. */
  async function apply(result: StepResult): Promise<void> {
    if (!result.ok) {
      // Any refusal: the user retypes the password, so no typed password stays in the page
      // (#284).
      setPassword("");
      if (realm === "operator" && (result.status === 404 || result.status === 503)) {
        // The release before the operators' own directory, or a stage without one: say so,
        // and never fall back to the tenant page's sign-in.
        setError(t("console.signIn.unavailable"));
        return;
      }
      if (result.error?.code === "flow_expired") {
        await startFlow(realm);
        setStep({ kind: "email" });
      }
      if (result.error?.code === "invalid_credentials") {
        // A refused code or passkey ends the server's challenge: back to the password.
        setCode("");
        setStep((current) =>
          current.kind === "totp" || current.kind === "passkey" || current.kind === "new_password"
            ? { kind: "credentials", passkey: offerPasskey.current }
            : current,
        );
      }
      setError(authErrorMessage(result));
      return;
    }
    setError(null);
    const next = result.step;
    switch (next.next) {
      case "redirect":
        // Named before leaving: the person sees which sign-in service the browser goes through.
        setStep({ kind: "redirecting", host: hostOf(next.url) });
        setTimeout(() => signInNavigation.go(next.url), 0);
        return;
      case "done":
        signInNavigation.go(safeReturnTo(next.returnTo));
        return;
      case "choice":
        setStep({ kind: "choice", displayName: next.displayName });
        return;
      case "credentials":
        offerPasskey.current = next.passkey && passkeys;
        setStep({ kind: "credentials", passkey: offerPasskey.current });
        return;
      case "totp":
      case "new_password":
        // The password was accepted: no copy kept, and the new one starts empty.
        setCode("");
        setPassword("");
        setStep({ kind: next.next });
        return;
      case "passkey":
        setStep({ kind: "passkey", options: next.options });
        await assert(next.options);
        return;
      case "enrol":
        setSetup(null);
        setSetupFailed(false);
        setStep({ kind: "enrol", methods: next.methods });
        if (!next.methods.includes("passkey") || !passkeys) {
          await loadTotp();
        }
        return;
    }
  }

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    try {
      await work();
    } finally {
      setBusy(false);
    }
  };

  // A link page hands over the step its password answered with, before the first paint (no
  // flash of the email step).
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, on mount
  useLayoutEffect(() => {
    if (props.initial) {
      void apply({ ok: true, step: props.initial.step });
    }
  }, []);

  // Back from the IdP restores this page from the back-forward cache: the email step again.
  useEffect(() => {
    const restored = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setStep((current) => (current.kind === "redirecting" ? { kind: "email" } : current));
      }
    };
    window.addEventListener("pageshow", restored);
    return () => window.removeEventListener("pageshow", restored);
  }, []);

  async function assert(options: Record<string, unknown>) {
    const passkey = auth.passkey;
    if (!passkey) {
      // The console offers no passkey: back to the password.
      setStep({ kind: "credentials", passkey: false });
      return;
    }
    let credential: Record<string, unknown>;
    try {
      credential = await getPasskey(options);
    } catch {
      setError(t("signIn.errors.passkeyCancelled"));
      setStep({ kind: "credentials", passkey: offerPasskey.current });
      return;
    }
    await apply(await call(() => passkey.assert({ credential })));
  }

  async function loadTotp() {
    setSetupFailed(false);
    try {
      const res = await auth.enrolTotp();
      if (res.ok) {
        setSetup(await res.json());
        return;
      }
      setSetupFailed(true);
      await apply(await readStep(res));
    } catch {
      setSetupFailed(true);
      setError(t("errors.network"));
    }
  }

  async function enrolPasskey() {
    const passkey = auth.passkey;
    if (!passkey) {
      return;
    }
    try {
      const res = await passkey.enrolOptions();
      if (!res.ok) {
        await apply(await readStep(res));
        return;
      }
      const { options } = await res.json();
      const credential = await createPasskey(options);
      await apply(await call(() => passkey.enrol({ credential })));
    } catch {
      setError(t("signIn.errors.passkeyCancelled"));
    }
  }

  const changeEmail = () => {
    setStep({ kind: "email" });
    setPassword("");
    setCode("");
    setSetup(null);
    setError(null);
  };

  const heading = stepHeading(step, realm);
  // The console enrols an authenticator app only (no passkey in this release).
  const passkeys = webAuthnSupported() && auth.passkey !== null;

  const signingInAs =
    step.kind === "email" || !email ? null : (
      <div className="flex items-center gap-3 rounded-lg border border-line bg-surface-raised py-2 pr-2 pl-2.5">
        <Avatar name={email} />
        <span className="min-w-0 flex-1 truncate text-sm text-fg">
          <span className="sr-only">{t("signIn.signingInAs")} </span>
          {email}
        </span>
        {/* The browser is already leaving: there is nothing left to change. */}
        {step.kind === "redirecting" ? null : (
          <Button type="button" variant="ghost" size="sm" onClick={changeEmail}>
            {t("signIn.changeEmail")}
          </Button>
        )}
      </div>
    );

  const usePasskey = (
    <Button
      type="button"
      variant="outline"
      disabled={busy}
      onClick={() =>
        void run(async () =>
          apply(
            await call(async () => {
              if (!auth.passkey) {
                throw new Error("No passkey on this page");
              }
              return auth.passkey.options();
            }),
          ),
        )
      }
    >
      <KeyRound aria-hidden="true" strokeWidth={1.75} />
      {t("signIn.usePasskey")}
    </Button>
  );

  return (
    <div className="flex flex-col gap-6">
      <StandaloneHeading title={heading.title} lead={heading.lead} />
      <div className="flex flex-col gap-4">
        {signingInAs}
        {error ? <Notice tone="danger">{error}</Notice> : props.notice}

        {step.kind === "email" ? (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () =>
                apply(
                  await call(() =>
                    auth.start({
                      email: email.trim(),
                      returnTo: returnTo.current,
                      // The tenant page offers the choice where the domain allows it (#419, D6).
                      ...(realm === "tenant" ? { method: "choice" as const } : {}),
                    }),
                  ),
                ),
              );
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-email`}>
                {realm === "operator" ? t("console.signIn.email") : t("signIn.email")}
              </Label>
              <Input
                id={`${id}-email`}
                type="email"
                name="email"
                required
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={error ? true : undefined}
                className="h-10"
              />
            </div>
            <Button type="submit" size="lg" disabled={busy}>
              {t("signIn.submit")}
            </Button>
          </form>
        ) : null}

        {step.kind === "choice" ? (
          // biome-ignore lint/a11y/useSemanticElements: two buttons, not a form's fieldset
          <div role="group" aria-labelledby={`${id}-choice`} className="flex flex-col gap-4">
            <span id={`${id}-choice`} className="sr-only">
              {t("signIn.choice.label", { email })}
            </span>
            <Button
              type="button"
              size="lg"
              disabled={busy}
              autoFocus
              onClick={() =>
                void run(async () =>
                  apply(
                    await call(() =>
                      auth.start({ email: email.trim(), returnTo: returnTo.current }),
                    ),
                  ),
                )
              }
            >
              {t("signIn.choice.continueWith", { name: step.displayName })}
            </Button>
            <OrRule />
            <Button
              type="button"
              variant="outline"
              size="lg"
              disabled={busy}
              onClick={() =>
                void run(async () =>
                  apply(
                    await call(() =>
                      auth.start({
                        email: email.trim(),
                        returnTo: returnTo.current,
                        method: "credentials",
                      }),
                    ),
                  ),
                )
              }
            >
              <KeyRound aria-hidden="true" strokeWidth={1.75} />
              {t("signIn.choice.credentials")}
            </Button>
          </div>
        ) : null}

        {step.kind === "redirecting" ? (
          <div className="flex flex-col gap-3" role="status">
            <p className="text-sm leading-relaxed text-fg-secondary">
              {t("signIn.steps.redirectLead")}
            </p>
            <p className="flex items-center gap-2.5 rounded-lg border border-line bg-surface-sunken px-3 py-2.5 text-sm font-medium break-all text-fg">
              <ExternalLink
                aria-hidden="true"
                className="size-4 shrink-0 text-fg-muted"
                strokeWidth={1.75}
              />
              {step.host}
              <span
                aria-hidden="true"
                className="ml-auto size-4 shrink-0 animate-spin rounded-full border-2 border-line-strong border-t-transparent motion-reduce:animate-none"
              />
            </p>
          </div>
        ) : null}

        {step.kind === "credentials" ? (
          <>
            <form
              className="flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                void run(async () => apply(await call(() => auth.password({ password }))));
              }}
            >
              {/* For password managers: the account this password belongs to. */}
              <input
                type="email"
                name="email"
                value={email}
                autoComplete="username"
                aria-label={t("signIn.email")}
                hidden
                readOnly
              />
              <div className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between gap-2">
                  <Label htmlFor={`${id}-password`}>{t("signIn.password")}</Label>
                  {/* Operators reset nothing here: an AWS administrator does (the console). */}
                  {realm === "operator" ? null : (
                    <Link
                      href={RESET_PASSWORD_PATH}
                      state={{ resetEmail: email.trim() } satisfies ResetHandoff}
                      className="text-xs text-accent-text underline-offset-4 hover:underline"
                    >
                      {t("signIn.forgot")}
                    </Link>
                  )}
                </div>
                <Input
                  id={`${id}-password`}
                  type="password"
                  name="password"
                  required
                  autoComplete="current-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={error ? true : undefined}
                  className="h-10"
                />
              </div>
              <Button type="submit" size="lg" disabled={busy}>
                {t("signIn.signIn")}
              </Button>
            </form>
            {step.passkey ? (
              <>
                <OrRule />
                {usePasskey}
              </>
            ) : null}
          </>
        ) : null}

        {step.kind === "totp" ? (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => apply(await call(() => auth.challenge({ type: "totp", code }))));
            }}
          >
            <CodeField
              id={`${id}-code`}
              value={code}
              onChange={setCode}
              invalid={!!error}
              disabled={busy}
            />
            <Button type="submit" size="lg" disabled={busy}>
              {t("signIn.verify")}
            </Button>
            <p className="text-xs leading-relaxed text-fg-muted">
              {realm === "operator" ? t("console.signIn.totpHelp") : t("signIn.totpHelp")}
            </p>
          </form>
        ) : null}

        {step.kind === "new_password" ? (
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!passwordLongEnough(password)) {
                setError(t("password.tooShort"));
                return;
              }
              void run(async () =>
                apply(await call(() => auth.challenge({ type: "new_password", password }))),
              );
            }}
          >
            <NewPasswordField value={password} onChange={setPassword} autoFocus />
            <Button type="submit" size="lg" disabled={busy}>
              {t("password.set")}
            </Button>
          </form>
        ) : null}

        {/* Mounted on every step and filled on the passkey one, as its browser dialog opens:
            a screen reader announces a change to a live region it already knows (the lead
            above is static). */}
        <p className="sr-only" aria-live="polite" data-testid="passkey-announcement">
          {step.kind === "passkey" ? t("signIn.passkeyWaiting") : null}
        </p>

        {step.kind === "passkey" ? (
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={() => void assert(step.options)}
          >
            <KeyRound aria-hidden="true" strokeWidth={1.75} />
            {t("signIn.usePasskey")}
          </Button>
        ) : null}

        {step.kind === "enrol" ? (
          setup ? (
            <div className="flex flex-col gap-4">
              <TotpSetup
                secret={setup.secret}
                uri={setup.uri}
                pending={busy}
                error={null}
                onConfirm={(value) =>
                  void run(async () =>
                    apply(await call(() => auth.enrolTotpVerify({ code: value }))),
                  )
                }
              />
              {step.methods.includes("passkey") && passkeys ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="self-start"
                  onClick={() => setSetup(null)}
                >
                  {t("signIn.enrol.other")}
                </Button>
              ) : null}
            </div>
          ) : (!step.methods.includes("passkey") || !passkeys) && !setupFailed ? (
            // One possible method: its setup opens directly (loading here).
            <div className="flex flex-col gap-3" role="status">
              <span className="sr-only">{t("app.loading")}</span>
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface-raised">
              <MethodRow
                icon={Smartphone}
                title={t("signIn.enrol.totp")}
                hint={t("signIn.enrol.totpHint")}
                disabled={busy}
                onClick={() => void run(loadTotp)}
              />
              {step.methods.includes("passkey") && passkeys ? (
                <MethodRow
                  icon={KeyRound}
                  title={t("signIn.enrol.passkey")}
                  hint={t("signIn.enrol.passkeyHint")}
                  disabled={busy}
                  onClick={() => void run(enrolPasskey)}
                />
              ) : null}
            </ul>
          )
        ) : null}
      </div>
    </div>
  );
}

/**
 * The e-mail of `?login_hint=` (the API's `/login` for a client that knows the choice, #419):
 * filled only, never submitted on its own. The tenant page only; a malformed one is ignored.
 */
function loginHint(realm: SignInRealm): string {
  if (realm !== "tenant") {
    return "";
  }
  const url = new URL(window.location.href);
  const hint = url.searchParams.get("login_hint");
  if (hint === null) {
    return "";
  }
  // Read once and taken off the address: it never reaches the page returned to, or the history.
  url.searchParams.delete("login_hint");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  return signInStartRequestSchema.shape.email.safeParse(hint).success ? hint : "";
}

/** Each step names itself: the heading says what to do now, its lead why. */
function stepHeading(step: FlowStep, realm: SignInRealm): { title: string; lead?: ReactNode } {
  switch (step.kind) {
    case "email":
      return realm === "operator"
        ? { title: translate("console.signIn.title"), lead: translate("console.signIn.lead") }
        : { title: translate("signIn.title"), lead: translate("signIn.lead") };
    case "redirecting":
      return { title: translate("signIn.steps.redirect") };
    case "choice":
      return { title: translate("signIn.steps.choice"), lead: translate("signIn.choice.lead") };
    case "credentials":
      return { title: translate("signIn.steps.password") };
    case "totp":
      return { title: translate("totp.title"), lead: translate("signIn.totpLead") };
    case "new_password":
      return {
        title: translate("signIn.steps.newPassword"),
        lead: translate("signIn.newPasswordLead"),
      };
    case "passkey":
      return { title: translate("signIn.steps.passkey"), lead: translate("signIn.passkeyWaiting") };
    case "enrol":
      return {
        title: translate("signIn.enrol.title"),
        lead: translate(realm === "operator" ? "console.signIn.enrolLead" : "signIn.enrol.lead"),
      };
  }
}

/** A hairline with "or" between two ways of signing in. */
function OrRule() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 text-xs text-fg-muted">
      <span aria-hidden="true" className="h-px flex-1 bg-line" />
      {t("signIn.or")}
      <span aria-hidden="true" className="h-px flex-1 bg-line" />
    </div>
  );
}

/** A second-factor method to set up: one row of the choice, the whole row a button. */
function MethodRow(props: {
  icon: LucideIcon;
  title: string;
  hint: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        disabled={props.disabled}
        onClick={props.onClick}
        className="flex w-full items-center gap-3 px-3.5 py-3 text-left transition-colors duration-(--duration-fast) hover:bg-surface-hover focus-visible:bg-surface-hover disabled:opacity-60"
      >
        <Avatar icon={props.icon} />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium text-fg">{props.title}</span>
          <span className="text-xs leading-relaxed text-fg-muted">{props.hint}</span>
        </span>
        <ChevronRight
          aria-hidden="true"
          className="size-4 shrink-0 text-fg-muted"
          strokeWidth={1.75}
        />
      </button>
    </li>
  );
}

/** The `?auth_error=` codes the API's sign-in callbacks redirect with (auth/routes.ts). */
const AUTH_ERROR_CODES = ["expired", "failed", "no_account", "seat_limit"] as const;

const isAuthErrorCode = (code: string | null): code is (typeof AUTH_ERROR_CODES)[number] =>
  (AUTH_ERROR_CODES as readonly (string | null)[]).includes(code);

/**
 * ESI's sign-in page (email first). A failed federated sign-in comes back with `?auth_error=`,
 * an ended session with `?auth_notice=expired`: both are announced above the form.
 */
export function SignIn({ realm = "tenant" }: { realm?: SignInRealm } = {}) {
  const { t } = useTranslation();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    void startFlow(realm).finally(() => setReady(true));
  }, [realm]);
  const params = new URLSearchParams(window.location.search);
  const error = params.get("auth_error");
  // Only the codes the API redirects with; anything else (a crafted link) gets a generic
  // message, never the link's own text (#165).
  const message = isAuthErrorCode(error)
    ? t(`signIn.errors.${error}`)
    : error
      ? t("signIn.errors.unknown")
      : null;
  const notice = message ? (
    <Notice tone="danger">{message}</Notice>
  ) : isEndReason(params.get("auth_notice")) ? (
    <Notice tone="warning" live>
      {t(`signIn.notice.${params.get("auth_notice") as SessionEndReason}`)}
    </Notice>
  ) : null;
  const heading = stepHeading({ kind: "email" }, realm);
  return (
    <StandaloneState console={realm === "operator"}>
      {ready ? (
        <SignInFlow notice={notice} realm={realm} />
      ) : (
        <>
          <StandaloneHeading title={heading.title} lead={heading.lead} />
          {notice}
        </>
      )}
    </StandaloneState>
  );
}
